import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../../lib/api";
import PlayerAvatar from "../../components/PlayerAvatar";
import { useAuth } from "../../contexts/AuthContext";
import { Star, ArrowRight, SoccerBall, Trophy, Medal, Shield, HandHeart, TrendUp } from "@phosphor-icons/react";

const AWARD_META = {
  player_of_month: { icon: Trophy, color: "#FFD166" },
  top_scorer: { icon: SoccerBall, color: "#D90429" },
  top_assister: { icon: HandHeart, color: "#FFD166" },
  best_attendance: { icon: Medal, color: "#06D6A0" },
  most_improved: { icon: TrendUp, color: "#FFD166" },
  best_defender: { icon: Shield, color: "#F5F5F5" },
  best_team_player: { icon: HandHeart, color: "#06D6A0" },
};

export default function ParentDashboard() {
  const { user } = useAuth();
  const [children, setChildren] = useState([]);

  useEffect(() => { api.get("/parent/children").then((r) => setChildren(r.data)); }, []);

  return (
    <div className="space-y-6">
      <div>
        <div className="overline">Welcome</div>
        <h1 className="font-display font-black text-4xl sm:text-5xl uppercase tracking-tight mt-1">
          Hi, {user?.name?.split(" ")[0] || "Parent"}
        </h1>
        <div className="text-neutral-400 text-sm mt-2">
          {children.length === 0
            ? "No player linked to your account yet. Please contact the coach."
            : `Following ${children.length} player${children.length > 1 ? "s" : ""}`}
        </div>
      </div>

      {children.length === 0 ? (
        <div className="bg-[#121212] border border-neutral-800 rounded-lg p-10 text-center">
          <SoccerBall size={40} weight="duotone" color="#D90429" className="mx-auto" />
          <div className="overline mt-4">Waiting for coach to link a player</div>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {children.map((p) => (
            <Link
              key={p.id}
              to={`/parent/players/${p.id}`}
              data-testid={`parent-child-${p.id}`}
              className="bg-[#121212] border border-neutral-800 rounded-lg p-5 card-lift"
            >
              <div className="flex items-center gap-4">
                <PlayerAvatar player={p} size={80} />
                <div className="flex-1 min-w-0">
                  <div className="font-display font-black text-2xl uppercase tracking-tight leading-tight">
                    {p.first_name} {p.last_name}
                  </div>
                  <div className="overline mt-1">
                    {p.position || "—"}{p.jersey_number ? ` · #${p.jersey_number}` : ""}
                  </div>
                  <div className="flex gap-4 mt-3 text-xs">
                    <span className="text-[#06D6A0] font-bold">{p.attendance_pct}% attendance</span>
                    <span className="flex items-center gap-1 text-[#FFD166] font-bold">
                      <Star size={12} weight="fill" /> {p.overall_rating || 0} / 5
                    </span>
                  </div>
                </div>
                <ArrowRight size={20} weight="bold" color="#737373" />
              </div>
              {p.awards?.length > 0 && (
                <div className="mt-4 pt-4 border-t border-neutral-800">
                  <div className="overline mb-2">Awards</div>
                  <div className="flex flex-wrap gap-2">
                    {p.awards.map((a) => {
                      const meta = AWARD_META[a.key] || { icon: Trophy, color: "#FFD166" };
                      const Icon = meta.icon;
                      return (
                        <span
                          key={a.key}
                          data-testid={`parent-award-${a.key}`}
                          className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-[#0a0a0a] border border-neutral-800 text-[11px] font-bold uppercase tracking-wider whitespace-nowrap"
                          style={{ color: meta.color }}
                        >
                          <Icon size={12} weight="fill" /> {a.label}
                        </span>
                      );
                    })}
                  </div>
                </div>
              )}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
